import { useNotification } from '@ctn/ui/components/ctn-notifications';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { notificationsConfig } from '@/helpers/notifications';
import type { IDirtyFormContext } from '@/interfaces/context/IDirtyForm.context.interface';

type DirtyProviderProps = {
	readonly children: ReactNode;
};

type IActionParam = {
	action: () => void;
};

const contextDefaultValue: IDirtyFormContext = {
	checkDirtyForm: () => undefined,
	checkDirtyFormCancelar: () => undefined,
	isDirtyForm: false,
	setIsDirtyForm: () => undefined,
};

const DirtyFormContext = createContext<IDirtyFormContext>(contextDefaultValue);

const DirtyProvider: React.FC<DirtyProviderProps> = ({ children }: DirtyProviderProps) => {
	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });
	const [isDirtyForm, setIsDirtyForm] = useState<boolean>(contextDefaultValue.isDirtyForm);
	const location = useLocation();

	const checkDirtyForm = ({ action }: IActionParam) => {
		if (isDirtyForm) {
			openNotification(notificationTemplate.dirtyForm(action));
		} else {
			action();
		}
	};

	const checkDirtyFormCancelar = ({ action, cancel }: IActionParam & { cancel: () => void }) => {
		if (isDirtyForm) {
			openNotification(notificationTemplate.cancelForm(action, cancel));
		} else {
			cancel();
		}
	};

	const value = useMemo(
		() => ({
			checkDirtyForm,
			checkDirtyFormCancelar,
			isDirtyForm,
			setIsDirtyForm,
		}),
		[isDirtyForm],
	);

	useEffect(() => {
		setIsDirtyForm(false);
	}, [location.pathname]);

	return <DirtyFormContext.Provider value={value}>{children}</DirtyFormContext.Provider>;
};

const useDirtyForm = () => {
	const context = useContext(DirtyFormContext);

	if (context === undefined) {
		throw new Error('useDirtyForm must be used within provider');
	}

	return context;
};

export { DirtyFormContext, DirtyProvider, useDirtyForm };
